<template>
  <div v-if="slides.length" class="app-carousel" :style="style">
    <div class="container main-carousel-box">
      <Swiper ref="swiperMain" class="main-carousel" :options="swiperMainOptions">
        <SwiperSlide v-for="slide in slides" :key="`${uniqueId}_main_${slide.id}`">
          <AppLink class="app-link" :to="slide.routeTo">
            <img
              v-if="slide.media"
              class="image"
              loading="lazy"
              :src="slide.media.url"
              :alt="slide.media.alt || slide.title"
            />
            <AppImageStub v-else class="image" />
          </AppLink>

          <div v-if="slide.title" class="slide-details">
            <AppCarouselSlideDetails :title="slide.title" :description="slide.description || slide.text">
              <template #title-prepend>
                <slot name="title-prepend" :slide="slide" />
              </template>
            </AppCarouselSlideDetails>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>

    <AppCarouselNavButton :id="navBtnIds.prev" type="prev" />
    <AppCarouselNavButton :id="navBtnIds.next" type="next" />
  </div>
</template>

<script>
import AppCarouselNavButton from '@/components/AppCarouselNavButton.vue';
import AppCarouselSlideDetails from '@/components/AppCarouselSlideDetails.vue';
import AppLink from '@/components/AppLink.vue';
import AppImageStub from '@/components/AppImageStub.vue';

export default {
  name: 'AppCarousel',
  components: { AppImageStub, AppLink, AppCarouselSlideDetails, AppCarouselNavButton },
  props: {
    uniqueId: {
      type: String,
      required: true,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    slides: {
      type: Array,
      required: true,
    },
    speed: {
      type: Number,
      default: 1000,
      validator: (value) => value >= 200,
    },
  },

  computed: {
    navBtnIds() {
      return {
        prev: `${this.uniqueId}_btn-prev`,
        next: `${this.uniqueId}_btn-next`,
      };
    },
    style() {
      return {
        '--app-carousel-swiper-speed': `${this.speed}ms`,
      };
    },
    swiper() {
      return this.$refs.swiperMain.swiper;
    },
    swiperMainOptions() {
      return {
        initialSlide: 0,
        loop: this.loop,
        preloadImages: false,
        navigation: {
          prevEl: `#${this.navBtnIds.prev}`,
          nextEl: `#${this.navBtnIds.next}`,
        },
        speed: this.speed,
        watchOverflow: true,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        autoHeight: true,

        slidesPerView: 1.15,
        spaceBetween: 15,
        centeredSlides: false,

        breakpoints: {
          768: {
            slidesPerView: 2,
            spaceBetween: 30,
            centeredSlides: true,
          },
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.app-carousel {
  --app-carousel-nav-btn-size: 70px;

  position: relative;
  overflow: hidden;

  .main-carousel-box {
    @media (max-width: vars.$tablet-break-point - 1) {
      padding: 0;
    }
  }

  .swiper-container {
    padding-left: 15px;
    padding-right: 15px;

    @media (min-width: vars.$tablet-break-point) {
      padding-left: 0;
      padding-right: 0;
    }
  }

  .main-carousel {
    margin-left: 0;

    @media (min-width: vars.$tablet-break-point) {
      width: auto;
      overflow: visible;
      margin-left: calc(-100% / 3 - 15px);
    }

    .swiper-slide {
      max-width: none;
      width: calc(100% / 1.15);
      opacity: 0.4;
      transition: opacity var(--app-carousel-swiper-speed, 1000ms) ease-in-out;

      @media (min-width: vars.$tablet-break-point) {
        width: calc(100% / 2);
      }
    }

    .swiper-slide-active,
    .swiper-slide-duplicate-active {
      opacity: 1;
    }

    .app-link {
      display: flex;
    }

    .image {
      width: 100%;
      height: auto;
      max-height: 600px;
      object-fit: cover;
      aspect-ratio: 758 / 570;
    }

    .slide-details {
      margin-top: 16px;
      overflow: hidden;
      opacity: 0;
      display: none;
      transition: opacity var(--app-carousel-swiper-speed, 1000ms) ease-in-out,
        display var(--app-carousel-swiper-speed, 1000ms) allow-discrete;
    }

    .swiper-slide-active,
    .swiper-slide-duplicate-active {
      .slide-details {
        opacity: 1;
        display: block;

        @starting-style {
          & {
            opacity: 0;
          }
        }
      }
    }
  }
}
</style>
